/**
 * @fileoverview 消息通知模块常量定义
 * 
 * 对应后端文件：
 * - backend/app/Modules/Notification/Models/Notification.php
 * - backend/app/Modules/Notification/Models/NotificationChannel.php
 * 
 * 注意事项：
 * - 常量名使用 UPPER_SNAKE_CASE 命名
 * - 所有常量都需要中文注释
 * - 为每个枚举生成对应的标签映射
 * 
 * 更新时机：
 * - 后端枚举值变更时
 * - 新增常量时
 * 
 * 验证方式：
 * - 对照后端 Model 常量定义
 * - 确保标签映射完整
 * 
 * @last-updated 2025-01-20
 */

import { NotificationType, NotificationLevel, NotificationChannel, ChannelStatus, TargetType } from './enums';

/**
 * 消息类型标签映射
 */
export const NOTIFICATION_TYPE_LABELS: Record<NotificationType, string> = {
  [NotificationType.TASK]: '任务消息',
  [NotificationType.ALERT]: '告警消息',
  [NotificationType.APPROVAL]: '审批消息',
  [NotificationType.SYSTEM]: '系统消息',
  [NotificationType.ORDER]: '订单消息',
  [NotificationType.NCR]: 'NCR通知',
  [NotificationType.DAILY_REPORT]: '日报消息',
};

/**
 * 消息级别标签映射
 */
export const NOTIFICATION_LEVEL_LABELS: Record<NotificationLevel, string> = {
  [NotificationLevel.INFO]: '信息',
  [NotificationLevel.WARNING]: '警告',
  [NotificationLevel.ERROR]: '错误',
  [NotificationLevel.SUCCESS]: '成功',
};

/**
 * 消息渠道标签映射
 */
export const NOTIFICATION_CHANNEL_LABELS: Record<NotificationChannel, string> = {
  [NotificationChannel.WECOM]: '企业微信',
  [NotificationChannel.APP]: 'App推送',
  [NotificationChannel.WEB]: 'Web推送',
  [NotificationChannel.SMS]: '短信',
  [NotificationChannel.EMAIL]: '邮件',
  [NotificationChannel.DINGTALK]: '钉钉',
};

/**
 * 渠道状态标签映射
 */
export const CHANNEL_STATUS_LABELS: Record<ChannelStatus, string> = {
  [ChannelStatus.PENDING]: '待发送',
  [ChannelStatus.SENT]: '已发送',
  [ChannelStatus.FAILED]: '发送失败',
};

/**
 * 目标类型标签映射
 */
export const TARGET_TYPE_LABELS: Record<TargetType, string> = {
  [TargetType.ALL]: '全部用户',
  [TargetType.DEPARTMENT]: '部门',
  [TargetType.USERS]: '指定用户',
};

/**
 * 消息类型选项
 */
export const NOTIFICATION_TYPE_OPTIONS = Object.values(NotificationType).map(type => ({
  value: type,
  label: NOTIFICATION_TYPE_LABELS[type],
}));

/**
 * 消息级别选项
 */
export const NOTIFICATION_LEVEL_OPTIONS = Object.values(NotificationLevel).map(level => ({
  value: level,
  label: NOTIFICATION_LEVEL_LABELS[level],
}));

/**
 * 消息渠道选项
 */
export const NOTIFICATION_CHANNEL_OPTIONS = Object.values(NotificationChannel).map(channel => ({
  value: channel,
  label: NOTIFICATION_CHANNEL_LABELS[channel],
}));

/**
 * 渠道状态选项
 */
export const CHANNEL_STATUS_OPTIONS = Object.values(ChannelStatus).map(status => ({
  value: status,
  label: CHANNEL_STATUS_LABELS[status],
}));

/**
 * 目标类型选项
 */
export const TARGET_TYPE_OPTIONS = Object.values(TargetType).map(type => ({
  value: type,
  label: TARGET_TYPE_LABELS[type],
}));

/**
 * 优先级选项
 */
export const PRIORITY_OPTIONS = [
  { value: 1, label: '低' },
  { value: 2, label: '普通' },
  { value: 3, label: '高' },
  { value: 4, label: '紧急' },
  { value: 5, label: '特急' },
];

/**
 * 默认消息设置
 */
export const DEFAULT_NOTIFICATION_SETTINGS = {
  /** 默认启用渠道 */
  DEFAULT_CHANNELS: [NotificationChannel.WEB, NotificationChannel.APP],
  /** 默认优先级 */
  DEFAULT_PRIORITY: 3,
  /** 默认消息类型 */
  DEFAULT_TYPE: NotificationType.SYSTEM,
  /** 默认消息级别 */
  DEFAULT_LEVEL: NotificationLevel.INFO,
};

/**
 * 消息模板变量
 */
export const NOTIFICATION_TEMPLATE_VARIABLES = {
  /** 用户相关变量 */
  USER: ['{user_name}', '{user_email}', '{user_phone}'],
  /** 系统相关变量 */
  SYSTEM: ['{system_name}', '{current_time}', '{current_date}'],
  /** 业务相关变量 */
  BUSINESS: ['{order_no}', '{product_name}', '{quantity}', '{amount}'],
  /** 审批相关变量 */
  APPROVAL: ['{approval_type}', '{approver_name}', '{approval_result}'],
};
